import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  Image,
  PasswordInput,
  Stack,
  Text,
} from '@mantine/core'
import { IconAlertCircle, IconKey } from '@tabler/icons-react'
import { useMantineColorScheme } from '@mantine/core'
import { stackedFor } from '@assets/logo'
import { APP_VERSION } from '../../AppConstants'

interface LoginProps {
  onLogin: (adminKey: string) => Promise<void> | void
  error?: string | null
}

/**
 * Um campo só: a chave de admin.
 *
 * Não há usuário nem senha porque o doclenz-api não tem modelo de usuário — o que existe é o
 * header `X-Admin-Key` conferido pelo `AdminController`. Ver README, seção "Autenticação".
 */
export function Login({ onLogin, error }: LoginProps) {
  const [adminKey, setAdminKey] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { colorScheme } = useMantineColorScheme()
  // O cartão acompanha o tema, então a marca também precisa acompanhar.
  const isDark = colorScheme === 'dark'

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!adminKey.trim() || submitting) return
    setSubmitting(true)
    try {
      await onLogin(adminKey.trim())
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Center h="100vh" bg={isDark ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-0)'}>
      <Card shadow="md" radius="md" p="xl" w={420} withBorder>
        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            <Stack gap={8} align="center">
              {/* A marca já traz o nome; um Title repetindo "Doclenz Admin" seria redundante. */}
              <Image
                src={stackedFor(isDark ? 'dark' : 'light')}
                alt="Doclenz"
                w={190}
                fit="contain"
              />
              <Text size="sm" c="dimmed">
                Console de administração
              </Text>
            </Stack>

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}

            {/* Sem descrição: a tela é pública e não deve revelar como a API é configurada. */}
            <PasswordInput
              label="Chave de admin"
              placeholder="dlens_admin_..."
              leftSection={<IconKey size={16} />}
              value={adminKey}
              onChange={(event) => setAdminKey(event.currentTarget.value)}
              autoFocus
              required
            />

            <Button type="submit" fullWidth loading={submitting} disabled={!adminKey.trim()}>
              Entrar
            </Button>

            <Group justify="center">
              <Text size="xs" c="dimmed">
                v{APP_VERSION}
              </Text>
            </Group>
          </Stack>
        </form>
      </Card>
    </Center>
  )
}
